import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api } from '@/services/api'
import { i18n } from '@/i18n'
import type { AppearanceSettings, LocaleCode, ThemeMode } from '@/types/monitoring'

const STORAGE_KEY = 'pulsewatch.appearance'
const defaults: AppearanceSettings = {
  theme: 'system',
  locale: 'en',
  faviconUrl: '',
  backgroundUrl: '',
}

function localSettings(): AppearanceSettings {
  try {
    return { ...defaults, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') }
  } catch {
    return { ...defaults }
  }
}

export const useAppearanceStore = defineStore('appearance', () => {
  const settings = ref<AppearanceSettings>(localSettings())
  const systemDark = ref(window.matchMedia('(prefers-color-scheme: dark)').matches)
  const initialized = ref(false)
  const isDark = computed(
    () =>
      settings.value.theme === 'dark' || (settings.value.theme === 'system' && systemDark.value),
  )

  function apply(): void {
    document.documentElement.classList.toggle('dark', isDark.value)
    document.documentElement.lang = settings.value.locale
    i18n.global.locale.value = settings.value.locale
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings.value))
    document.body.style.setProperty(
      '--dashboard-background-image',
      settings.value.backgroundUrl
        ? `url(${JSON.stringify(settings.value.backgroundUrl)})`
        : 'none',
    )
    let favicon = document.querySelector<HTMLLinkElement>('link[data-pulsewatch-favicon]')
    if (settings.value.faviconUrl) {
      if (!favicon) {
        favicon = document.createElement('link')
        favicon.rel = 'icon'
        favicon.dataset.pulsewatchFavicon = 'true'
        document.head.appendChild(favicon)
      }
      favicon.href = settings.value.faviconUrl
    } else {
      favicon?.remove()
    }
  }

  async function initialize(token: string, authenticated = Boolean(token)): Promise<void> {
    apply()
    if (!authenticated || initialized.value) return
    settings.value = await api.appearance(token)
    initialized.value = true
    apply()
  }

  async function save(token: string, value: AppearanceSettings): Promise<void> {
    settings.value = await api.updateAppearance(token, value)
    apply()
  }

  function setTheme(theme: ThemeMode): void {
    settings.value = { ...settings.value, theme }
    apply()
  }

  function setLocale(locale: LocaleCode): void {
    settings.value = { ...settings.value, locale }
    apply()
  }

  const media = window.matchMedia('(prefers-color-scheme: dark)')
  media.addEventListener('change', (event) => {
    systemDark.value = event.matches
    apply()
  })
  apply()

  return { settings, initialized, isDark, initialize, save, setTheme, setLocale }
})
